import type { AccountPlan } from '../../global';
import { t } from '../../lib/i18n';
import { IS_NATIVE } from '../../lib/platform';

/**
 * Abonelik planları (Ayarlar > Abonelik). 2026-10-05: botlu/botsuz ayrımı yok,
 * her planda toplantı botu var.
 *
 *   kimlik     görünen ad  fiyat    saat
 *   basic      Basic       $0         5
 *   pro        Pro         $14.99    20
 *   plus       Ultra       $29.99    50
 *   enterprise Kurumsal    teklif     —
 *
 * Kimlikler veritabanında ve iyzico eşlemesinde (IYZICO_PLAN_REF_*) duruyor;
 * kimlik ≠ görünen ad (plus = "Ultra"). Hesabın planı ve kotası veritabanında
 * (profiles.plan, migrations/0045); iyzico'daki gerçek fiyat tanımlıysa kart
 * onu gösterir, buradaki fiyat yalnızca liste fiyatı.
 */

/** Yıllık ödemede uygulanan indirim. */
export const YEARLY_DISCOUNT = 0.2;

/** Kurumsal plan ve sorular için iletişim adresi. */
export const CONTACT_EMAIL = 'merhaba@talknote.net';

export interface PlanCard {
  id: AccountPlan;
  name: string;
  /** Aylık fiyat (USD). Enterprise'ta fiyat gösterilmiyor. */
  monthlyPrice: number | null;
  /** Aylık kayıt saati. Enterprise'ta sözleşmeye göre. */
  hours: number | null;
  /** Fiyatın altındaki küçük açıklama. */
  priceNote: string;
  /** Madde listesinin başlığı. */
  featuresTitle: string;
  features: string[];
}

/** Plan adı — kenar çubuğu ve abonelik ekranı aynı kaynaktan okuyor. */
export function planName(plan: AccountPlan): string {
  switch (plan) {
    case 'pro':
      return 'Pro';
    case 'plus':
      return 'Ultra';
    case 'enterprise':
      return t('Kurumsal', 'Enterprise');
    default:
      return 'Basic';
  }
}

/** Planın aylık kayıt saati — kota veritabanından geliyor, bu yalnızca kart metni. */
export function planHours(plan: AccountPlan): number | null {
  switch (plan) {
    case 'pro':
      return 20;
    case 'plus':
      return 50;
    case 'enterprise':
      return null;
    default:
      return 5;
  }
}

/** Ücretli planların ortak maddeleri — yalnızca saat farklı. */
function paidFeatures(hours: number): string[] {
  return [
    t(`Ayda ${hours} saat kayıt ve transkript, bot toplantıları dahil`, `${hours} hours of recording & transcription per month, bot meetings included`),
    t('Özet e-postası ve arşiv dışa aktarımı', 'Summary email and archive export'),
    t('Özet dili ve özet stilleri', 'Summary language and summary styles'),
    t('Öncelikli destek', 'Priority support'),
  ];
}

export function planCards(): PlanCard[] {
  return [
    {
      id: 'basic',
      name: planName('basic'),
      monthlyPrice: 0,
      hours: 5,
      priceNote: t('her zaman ücretsiz', 'free forever'),
      featuresTitle: t('İçerir', 'Includes'),
      features: [
        t('Ayda 5 saat kayıt ve transkript', '5 hours of recording & transcription per month'),
        t('Toplantılara bot gönderme (Meet, Zoom, Teams)', 'Send a bot to meetings (Meet, Zoom, Teams)'),
        t('Takvimden otomatik bot katılımı', 'Automatic bot joining from your calendar'),
        IS_NATIVE
          ? t('Telefondan kayıt (ekran kilitliyken de)', 'Records on your phone (even with the screen locked)')
          : t('Bilgisayardan kayıt (mikrofon + sistem sesi)', 'Records on your computer (microphone + system audio)'),
        t('Yapay zekâ özeti, kararlar ve aksiyon maddeleri', 'AI summary, decisions and action items'),
        t('Google ve Outlook takvim bağlantısı', 'Google and Outlook calendar sync'),
        t('Kişiler ve şirketler arşivi', 'People and companies archive'),
      ],
    },
    {
      id: 'pro',
      name: planName('pro'),
      monthlyPrice: 14.99,
      hours: 20,
      priceNote: t('aylık', 'per month'),
      featuresTitle: t('Basic’teki her şey, artı', 'Everything in Basic, plus'),
      features: paidFeatures(20),
    },
    {
      id: 'plus',
      name: planName('plus'),
      monthlyPrice: 29.99,
      hours: 50,
      priceNote: t('aylık', 'per month'),
      featuresTitle: t('Pro’daki her şey, artı', 'Everything in Pro, plus'),
      features: [t('Ayda 50 saat kayıt ve transkript, bot toplantıları dahil', '50 hours of recording & transcription per month, bot meetings included')],
    },
    {
      id: 'enterprise',
      name: planName('enterprise'),
      monthlyPrice: null,
      hours: null,
      priceNote: t('size özel fiyat', 'custom pricing'),
      featuresTitle: t('Ultra’daki her şey, artı', 'Everything in Ultra, plus'),
      features: [
        t('Özel kayıt limiti ve saklama süresi', 'Custom recording limit and retention'),
        t('Tek oturum açma (SSO) ve yönetici denetimi', 'Single sign-on (SSO) and admin controls'),
        t('Kurumsal sözleşme ve fatura', 'Company contract and invoicing'),
        t('Adanmış destek ve ekip eğitimi', 'Dedicated support and team onboarding'),
      ],
    },
  ];
}

/** Yıllık ödemede aylık karşılık (ör. 14.99 → 11.99). */
export function effectiveMonthlyPrice(monthlyPrice: number, yearly: boolean): number {
  return yearly ? monthlyPrice * (1 - YEARLY_DISCOUNT) : monthlyPrice;
}

/** $0 / $14.99 — kuruş yoksa da iki hane gösteriliyor. */
export function formatUsd(amount: number): string {
  return amount === 0 ? '$0' : `$${amount.toFixed(2)}`;
}
